import {
  getMarketValueVolume,
  getMaxDigitValueForAmount,
  getProtectionValue,
  getProtectionValueRange,
} from './checkCreatingOrder';

import { IInstrument, ITradingModelFE } from '../types';

import { TPlaceOrderSide } from '@trader/types';
import { formatByPipSize, getMaxDigitWithDot } from '@trader/utils';
import { calculateIndicesPips } from './indicesPips';

export const getRealTimePrice = (data: {
  ask: number;
  bid: number;
  pipSize: number;
}) => {
  const ask = formatByPipSize(data.ask, data.pipSize);
  const bid = formatByPipSize(data.bid, data.pipSize);

  return {
    ask: ask,
    bid: bid,
    currentPrice: ask,
  };
};

export const getTradingModelFE = (
  data: ITradingModelFE,
  side: TPlaceOrderSide
): IInstrument => {
  const maxDigitAmount = getMaxDigitValueForAmount(data.minOrderSize);
  const maxDigit = getMaxDigitWithDot(data.pipSize);

  const difference = data.stopsLevel * data.pipSize;

  const buyValue = data.limitOrderBuyValue || Number(data.bid) + difference;
  const sellValue = data.limitOrderSellValue || Number(data.ask) - difference;

  const getTradingHours = () => {
    const day = new Date().getDay();

    if (
      !data.sessions ||
      !data.sessions[day] ||
      !data.sessions[day].tradingHours
    ) {
      return [];
    }

    return data.sessions[day].tradingHours;
  };

  return {
    general: {
      id: data.id,
      symbol: data.symbol,
      step: data.minOrderSizeIncrement,
      pipSize: data.pipSize,
      fixDigitAfterDot: maxDigitAmount,
      description: data.description,
      category: data.category,
      currency: data.currency,
      leverage: 1,
      stopsLevel: data.stopsLevel,
      spreadDiff: data.spreadDiff,
      open: Number(data.open),
      isOpenTrading: data.isOpenTrading,
      disabledByMarketNews: data.disabledByMarketNews,
      swapRateLong: data.swapRateLong,
      swapRateShort: data.swapRateShort,
      displayName: data.displayName,
      currencySymbol: data.currencySymbol,
      iconUrl: data.iconUrl,
      isFavorite: data.isFavorite,
      tradingHours: getTradingHours(),
      high: data.high,
      low: data.low,
      close: data.close,
      tradingStatus: data.tradingStatus,
    },
    market: {
      billType: side,
      amountVolume: data.minOrderSize.toString(),
      valueVolume: getMarketValueVolume(
        side,
        data.bid,
        data.ask,
        data.minOrderSize,
        data.pipSize
      ).toString(),
      minAmountValue: data.minOrderSize,
      maxAmountValue: data.maxOrderSize,
      reqMarginValue: 0,
      protection: {
        isSwitchProfit: false,
        profitPips: calculateIndicesPips(data.stopsLevel, 'toPips') || '0',
        stopLossPips: calculateIndicesPips(data.stopsLevel, 'toPips') || '0',
        stopsLevel: data.stopsLevel || 0,
        isSwitchLoss: false,
        unitStopLoss: 'Rate',
        unitProfit: 'Rate',
        lossValue: getProtectionValue(
          side,
          String(data.bid),
          String(data.ask),
          maxDigit,
          data.pipSize,
          data.stopsLevel
        ).loss,
        profitValue: getProtectionValue(
          side,
          String(data.bid),
          String(data.ask),
          maxDigit,
          data.pipSize,
          data.stopsLevel
        ).profit,
        minValue: getProtectionValueRange(
          side,
          String(data.bid),
          data.minOrderSizeIncrement,
          String(data.ask)
        ).min,
        maxValue: getProtectionValueRange(
          side,
          String(data.bid),
          data.minOrderSizeIncrement,
          String(data.ask)
        ).max,
      },
    },
    limit: {
      billType: side,
      buyValue: formatByPipSize(buyValue, data.pipSize),
      sellValue: formatByPipSize(sellValue, data.pipSize),
      amount: data.minOrderSize.toString(),
      value: getMarketValueVolume(
        side,
        data.bid,
        data.ask,
        data.minOrderSize,
        data.pipSize
      ).toString(),
      reqMargin: 0,
    },
  };
};
